import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateExpenseCategoryFormValues,
  UpdateExpenseCategoryFormValues,
  createExpenseCategory,
  deleteExpenseCategory,
  getExpenseCategories,
  restoreExpenseCategory,
  updateExpenseCategory,
} from "../api/expenseCategories";
import { queryClient } from "@/api/queryClient";
import { ExpenseCategory } from "@/types/expenseCategory";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { AxiosRequestConfig } from "axios";

interface useExpenseCategoryMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseCategories = (config?: AxiosRequestConfig) => {
  return useQuery<
    PaginatedApiResponse<ExpenseCategory[]>,
    Error,
    { data: ExpenseCategory[]; pagination: PaginationInfo }
  >({
    queryKey: ["expense-categories", config?.params],
    queryFn: () => getExpenseCategories(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetExpenseCategoriesOptions = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<ExpenseCategory[]>, Error, ExpenseCategory[]>({
    queryKey: ["expense-categories-options", config?.params],
    queryFn: () => getExpenseCategories(config),
    select: (response) => response.data,
  });
};

export const useCreateExpenseCategory = (
  options?: useExpenseCategoryMutationOptions<ExpenseCategory>
) => {
  return useMutation({
    mutationFn: (data: CreateExpenseCategoryFormValues) =>
      createExpenseCategory(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useUpdateExpenseCategory = (
  options?: useExpenseCategoryMutationOptions<ExpenseCategory>
) => {
  return useMutation({
    mutationFn: (data: UpdateExpenseCategoryFormValues) =>
      updateExpenseCategory(data.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useDeleteExpenseCategory = (
  options?: useExpenseCategoryMutationOptions<ExpenseCategory>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteExpenseCategory(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useRestoreExpenseCategory = (
  options?: useExpenseCategoryMutationOptions<ExpenseCategory>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => restoreExpenseCategory(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-categories"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};
